'use client'
import { SbBlokData, storyblokEditable } from '@storyblok/react'
import { FC, useEffect, useState } from 'react'

//import StoryblokClient from 'storyblok-js-client'
import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { setDate } from '@/lib/date'
import { calculateReadingTime } from '@/lib/readingTime'
import { getStoryblokApi } from '@/lib/storyblok-api'
//const Storyblok = new StoryblokClient({ accessToken: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN })
import { storyblokRichText } from '@/utilities/richText'
import { storyHref } from '@/utils/url'

import { PhnxFeaturedarticles } from './Featuredarticles'
import styles from './Featuredarticles.module.css'

interface FeaturedarticlesBlokProps {
  blok: { showFilter: boolean; featured_article: any[]; sub_articles: any[] } & SbBlokData
}

// TODO: This should be reusable and moved to a helper file
function ArticleReadTime(body: any[] | undefined) {
  if (!body || !body.length || !body[0]?.content) {
    return '0 min read'
  }

  try {
    // Render the first block's content using the new resolver
    const html = storyblokRichText.render(body[0].content)
    return calculateReadingTime(html)
  } catch (error) {
    console.error('Error calculating reading time:', error)
    return '0 min read'
  }
}

const Featuredarticles: FC<FeaturedarticlesBlokProps> = ({ blok }) => {
  const [feature, setSetFeature] = useState<any>([])
  const [cardItems, setCardItems] = useState<any[]>([])
  const [topics, setTopics] = useState<any[]>([])
  const [types, setTypes] = useState<any[]>([])
  const [searchText, setSearchText] = useState<string>('')

  const fetchTopics = async () => {
    const storyblokApi = getStoryblokApi()
    const response = await storyblokApi.get('cdn/datasource_entries', {
      datasource: 'article-tags',
      token: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN,
      per_page: 100,
    })
    setTopics(response.data.datasource_entries)
  }

  const fetchTypes = async () => {
    const storyblokApi = getStoryblokApi()
    const response = await storyblokApi.get('cdn/datasource_entries', {
      datasource: 'article-type',
      token: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN,
      per_page: 100,
    })
    response.data.datasource_entries = response.data.datasource_entries.map((type: any) => {
      type.title = type.name + 's'
      return type
    })
    setTypes(response.data.datasource_entries)
  }

  useEffect(() => {
    fetchTopics()
    fetchTypes()
  }, [])

  function setCardData(article: any, type?: string) {
    const content = article?.content ?? {}

    // make sure tags is always an array
    let tags: string[] = Array.isArray(content.tags) ? content.tags : []

    if (type === 'sub') {
      if (content.article_type === 'Blog Post') {
        tags = ['Article']
      } else if (typeof content.article_type === 'string') {
        tags = content.article_type.split(' ')
      }
    }

    return {
      image: {
        src: content.featured_image?.filename ?? '',
        alt: content.featured_image?.alt ?? '',
        focus: content.featured_image?.focus ?? '',
      },
      title: content.title ?? 'Untitled',
      link: storyHref(article?.full_slug),
      date: content.date ? setDate(content.date) : '',
      timeToRead: content.body ? ArticleReadTime(content.body) : '',
      tags,
    }
  }

  useEffect(() => {
    const featuredArticle = blok?.featured_article?.[0]
    if (featuredArticle?.content) {
      setSetFeature(setCardData(featuredArticle, 'featured'))
    } else {
      setSetFeature(null) // or leave empty state
    }

    const relatedArticles = blok?.sub_articles ?? []
    const items = relatedArticles
      .filter((article: any) => article?.content) // guard here
      .map((article: any) => setCardData(article, 'sub'))

    setCardItems(items)
  }, [blok?.featured_article, blok?.sub_articles])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const urlParams = new URLSearchParams(window.location.search)
    const paramName = e.target.name
    urlParams.set(paramName, e.target.value)
    const newUrl = `/insights/search?${urlParams.toString()}`
    window.location.href = newUrl
  }

  const handleSearch = () => {
    const urlParams = new URLSearchParams(window.location.search)
    urlParams.set('search', searchText)
    const newUrl = `/insights/search?${urlParams.toString()}`
    window.location.href = newUrl
  }

  return (
    <div {...storyblokEditable(blok)}>
      {blok.showFilter && (
        <div className={`${styles.searchfilter}`}>
          <div className="container">
            <div className={styles.filter}>
              <div className={styles.item}>
                <label htmlFor="topics">Topics</label>
                <select name="topic" id="topics" onChange={handleChange}>
                  <option value="all">All</option>
                  {topics.map((topic, index) => (
                    <option key={index} value={topic.name}>
                      {topic.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.item}>
                <label htmlFor="content-type">Content Type</label>
                <select name="contentType" id="content-type" onChange={handleChange}>
                  <option value="all">All</option>
                  {types
                    .filter((type) => type.title !== 'Reports')
                    .map((type, index) => (
                      <option key={index} value={type.name}>
                        {type.title}
                      </option>
                    ))}
                </select>
              </div>
              <div className={styles.item}>
                <label htmlFor="sort">Sort</label>
                <select name="sort" id="sort" onChange={handleChange}>
                  <option value="relevance">Relevance</option>
                  <option value="datedesc">Date Desc</option>
                  <option value="dateasc">Date Asc</option>
                </select>
              </div>
              <div className={`${styles.item} ${styles.searchText}`}>
                <label htmlFor="search" className="sr-only">
                  Free Text
                </label>
                <input
                  type="text"
                  name="search"
                  id="search"
                  placeholder="Search for insights"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className={styles.search}>
                <PhnxButton type="button" label="" icon={'fx-icon-search'} onClick={handleSearch} />
              </div>
            </div>
          </div>
        </div>
      )}
      <PhnxFeaturedarticles featured={feature} items={cardItems} />
    </div>
  )
}

export default Featuredarticles
